import React, { type ComponentType } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
// Library CSS is imported from the entry so it lands in the entry stylesheet,
// which precedes component and page CSS in the cascade regardless of how the
// app code is chunked: page rules at equal specificity must win over library
// defaults. (@mantine/dropzone/styles.css loads via an @import in index.css;
// @mantine/charts/styles.css is in App.tsx, the only app that renders charts.)
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import env from 'env';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { SWRConfig } from 'swr';
import { swrGlobalConfig } from 'api/swrConfig';
import { configureAppTransport } from 'api/appTransport';
import {
  externalAgentTransportPolicy,
  internalTransportPolicy,
  interviewTransportPolicy,
  jobBoardTransportPolicy,
  recruiterTransportPolicy,
} from 'api/appTransportPolicies';

import { appLoadFailureMessage } from './chunkErrorFallbackText';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { bootRecruiterSession } from './app-shell/recruiterSessionBoot';
import {
  isAuthenticatedUser,
  beforeSendRumEvent,
  type CandidateRumContext,
} from './utils/rumUtils';
import {
  isChunkLoadError,
  isReloadInitiated,
  recoverFromPreloadError,
  reloadOnceForChunkError,
  reportUnrecoveredChunkError,
} from './utils/chunkReload';
import { startSessionReplayWhenIdle } from './utils/sessionReplay';
import { isSharedPagePath } from './utils/sharedPagePath';
import {
  prefetchLocale,
  whenInitialLocaleRegistered,
} from './i18nLocaleLoader';

// Chrome-only APIs that the DOM typings leave out.
interface NavigatorWithDeviceMemory extends Navigator {
  readonly deviceMemory?: number;
}
interface PerformanceWithMemory extends Performance {
  readonly memory?: { readonly jsHeapSizeLimit: number };
}

/** An app root module, loaded by the dynamic imports at the bottom. */
interface AppModule {
  default: ComponentType;
}

/** The recruiter route warm-up, loaded by the dynamic import at the bottom. */
interface RouteWarmupModule {
  startRecruiterRouteWarmup: (location: Location) => void;
}

datadogRum.init({
  applicationId: env.REACT_APP_DATADOG_RUM_APPLICATION_ID,
  clientToken: env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN,
  site: 'datadoghq.com',
  service: 'web-frontend',
  env: env.REACT_APP_ENVIRONMENT,
  version: import.meta.env.APP_VERSION.toLowerCase(),
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackLongTasks: true, // already the v6 default; explicit so the long-task dependency is visible
  defaultPrivacyLevel: 'allow', // mask-user-input is the another option
  // The recorder otherwise serializes the DOM and installs its MutationObserver
  // during init, so it records the React mount as a mutation storm while the app
  // is still booting. startSessionReplayRecording below runs it after mount.
  startSessionReplayRecordingManually: true,
  plugins: [reactPlugin({ router: true })],
  beforeSend: beforeSendRumEvent,
});

// Set candidate RUM context immediately on page load before React mounts
const currentPath = window.location.pathname;
const isWebInterview = currentPath.includes('/web-interview/');

if (isWebInterview) {
  // Only track real candidates, not authenticated users testing interviews
  if (!isAuthenticatedUser()) {
    const pathParts = currentPath.split('/');
    const encodedParam = pathParts[pathParts.length - 1];

    // Set initial RUM context with whatever we can extract from URL
    const initialContext: CandidateRumContext = {
      user_type: 'candidate',
      session_stage: 'initial_page_load',
      url_path: currentPath,
      timestamp_loaded: new Date().toISOString(),
    };

    // Detect if this is a test call vs real interview
    if (currentPath.includes('/test-call/')) {
      initialContext.is_test_call = true;
      initialContext.encoded_test_data = encodedParam;
    } else if (encodedParam && encodedParam !== 'error') {
      initialContext.is_test_call = false;
      initialContext.encoded_id = encodedParam;
    }

    // Add any URL query parameters for additional context
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.toString()) {
      initialContext.url_params = searchParams.toString();
    }

    datadogRum.setUser(initialContext);

    // Device-capability baseline for correlating crashes with weak hardware.
    // hardwareConcurrency is widely supported; deviceMemory and
    // performance.memory are Chrome-only and may be undefined elsewhere.
    const navigatorWithMemory: NavigatorWithDeviceMemory = navigator;
    const performanceWithMemory: PerformanceWithMemory = performance;
    const deviceCapability = {
      hardware_concurrency: navigator.hardwareConcurrency ?? null,
      device_memory: navigatorWithMemory.deviceMemory ?? null,
      js_heap_limit_mb: performanceWithMemory.memory
        ? Math.round(
            performanceWithMemory.memory.jsHeapSizeLimit / (1024 * 1024)
          )
        : null,
    };
    // Stamp every event in the session (views, long_tasks, errors) so crash and
    // long-task queries can be faceted by hardware directly, without joining
    // back to this one-shot action.
    Object.entries(deviceCapability).forEach(([key, value]) =>
      datadogRum.setGlobalContextProperty(key, value)
    );

    // Add custom context for easier filtering in Datadog
    datadogRum.addAction('candidate_session_started', {
      page_load_time: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      ...deviceCapability,
    });
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('index.html has no #root element');
}
const root = ReactDOM.createRoot(rootElement);

// Each app is loaded with a dynamic import so the entry chunk stays tiny and a
// build only ships its own app: REACT_APP_SALV_APP is a compile-time constant,
// so the branches not taken (and their chunks) are dropped from the bundle.
const renderApp = ({ default: SelectedApp }: AppModule): void => {
  // The reload guard is re-armed by AppErrorBoundary after a stable, error-free
  // window — not here, since the entry chunk loading says nothing about whether
  // the React tree (and its lazy routes) actually mounts.
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        <SWRConfig value={swrGlobalConfig}>
          <SelectedApp />
        </SWRConfig>
      </AppErrorBoundary>
    </React.StrictMode>
  );
  // A share-link page is never recorded (SharedSubmittalReportRoute also stops a recording
  // already running when the app navigates there).
  if (
    !isSharedPagePath(window.location.pathname) &&
    window.location.pathname !== '/external-agent/chat'
  )
    startSessionReplayWhenIdle();
};

// The language the app was about to render in (the ?lang= parameter, or the
// recruiter's cached display language), for the entry failure message.
let appLoadLanguage: string | null = null;

const renderAppLoadFailure = (error: unknown): void => {
  console.error('Failed to load app chunk', error);
  // A failed entry chunk is the same deploy-skew failure mode; reload once to
  // pull the current build before giving up. The preload listener sees this
  // same failure first and reloads on it, so by the time we get here the guard
  // is usually spent: without the pending-reload check we would report a
  // recovery that is underway and paint the error over a page that is about to
  // navigate away. Nothing registers beforeunload before the app mounts, so
  // that reload cannot be cancelled by a prompt and needs no cancellation hook.
  if (
    isChunkLoadError(error) &&
    (reloadOnceForChunkError(error, 'app_entry') || isReloadInitiated())
  ) {
    return;
  }
  datadogRum.addError(error, { feature: 'app_entry_chunk_load' });
  const style =
    'display:flex;align-items:center;justify-content:center;height:100vh;' +
    'font-family:Inter,system-ui,sans-serif;text-align:center;padding:16px;color:#333';
  rootElement.innerHTML = `<div style="${style}">${appLoadFailureMessage(appLoadLanguage)}</div>`;
};

// Vite dispatches `vite:preloadError` when a preload fetch fails (the
// fetch-failure flavor of deploy skew, before React sees the rejected import).
// Only the stylesheet branch has its default prevented: preventing it on the
// module branch makes Vite resolve the import with `undefined` instead of
// rejecting. See recoverFromPreloadError.
window.addEventListener('vite:preloadError', (event) => {
  if (recoverFromPreloadError(event.payload)) {
    event.preventDefault();
  }
});

// Dynamic imports outside React's render path (e.g. prefetchLocale) reject to
// an unhandled rejection rather than the error boundary; recover those too.
// preventDefault only silences the console. RUM still records the rejection,
// and beforeSend drops the dynamic-import copy. reportUnrecoveredChunkError
// sends one custom error when a reload is not going to clear it, and skips
// HTML-as-script because that browser copy is kept.
window.addEventListener('unhandledrejection', (event) => {
  const reason: unknown = event.reason;
  if (!isChunkLoadError(reason)) return;
  const reloaded = reloadOnceForChunkError(reason, 'unhandled_rejection');
  if (reloaded || isReloadInitiated()) {
    event.preventDefault();
  }
  reportUnrecoveredChunkError(reason, 'unhandled_rejection_chunk_load');
});

// How long the first render may wait on a locale.
const LOCALE_GATE_TIMEOUT_MS = 2000;

// Neither race member rejects, so callers' catch still sees only import and
// render failures.
const renderWhenReady = (
  module: AppModule,
  whenReady: Promise<void>
): Promise<void> =>
  Promise.race([
    whenReady,
    new Promise<void>((resolve) =>
      window.setTimeout(resolve, LOCALE_GATE_TIMEOUT_MS)
    ),
  ]).then(() => renderApp(module));

// Used by the two candidate-facing apps. Their bundle has to be registered
// with i18next before the first commit or the page paints English and swaps a
// beat later, so the render waits on the loader's handshake. The chunk starts
// downloading before the app import, so the app chunk is normally the long pole
// and the wait costs nothing; the race bounds it, and a locale that is slow or
// never arrives costs a moment and then renders in English. Recruiter imports
// ./i18n too but never waits on a ?lang= fetch; it gates on the user's cached
// display language instead (renderRecruiterApp). Internal never waits on a
// locale: it renders in English and switches once its lazy recruiter-i18n
// chunk applies the user's language.
const renderWhenLocaleReady = (loadApp: () => Promise<AppModule>): void => {
  const langParam = new URLSearchParams(window.location.search).get('lang');
  appLoadLanguage = langParam;
  void prefetchLocale(langParam);
  // loadApp is a thunk so its import specifier stays a static literal inside
  // the compile-time branch below, which is what lets the build drop the other
  // apps' chunks. The timeout is armed here rather than alongside the prefetch
  // so it bounds the wait itself: started earlier, an app chunk slower than the
  // cap would spend the whole budget before the gate is even reached. Neither
  // race member rejects, so the catch still sees only import and render
  // failures.
  loadApp()
    .then((module) =>
      renderWhenReady(module, whenInitialLocaleRegistered(langParam))
    )
    .catch(renderAppLoadFailure);
};

// The recruiter app renders in the display language cached by the user's last
// session. Its bundles start downloading alongside the app chunk, and App.tsx
// applies them at module scope; the first render waits on that (bounded) so
// the page does not paint English and swap. English, the default, never waits.
// The loader is i18next-free, so this adds no library code to the entry.
const renderRecruiterApp = (
  loadApp: () => Promise<AppModule>,
  loadRouteWarmup: () => Promise<RouteWarmupModule>
): void => {
  const appModule = loadApp();
  // Starts /auth/verify (and the cached-session render state) without waiting
  // for the app chunk. Imported statically: everything it uses is already in the
  // entry's graph, and a separate chunk would cost a round trip before the
  // request. App.tsx boots it again, a no-op once this ran.
  bootRecruiterSession();
  // Then English `common`, the route's page chunk and the tab chunks it renders
  // straight away, so they download alongside the app chunk instead of after
  // it. A task rather than a call: axios sends the verify request from a
  // microtask, once this module has finished evaluating, and the warm-up's
  // preload links take 80 ms or more of main thread at a 4x CPU slowdown,
  // which would otherwise hold that request back.
  window.setTimeout(() => {
    loadRouteWarmup()
      .then(({ startRecruiterRouteWarmup }) =>
        startRecruiterRouteWarmup(window.location)
      )
      .catch((error: unknown) => {
        // swallow-ok: an optimization only; the router and the page load each chunk themselves
        console.warn('Recruiter route warm-up did not start', error);
      });
  }, 0);
  import('./recruiter-i18n/localeLoader')
    .then((loader) => {
      const cached = loader.readCachedRecruiterLanguage();
      appLoadLanguage = cached;
      if (!loader.needsRecruiterLocaleFetch(cached)) {
        return appModule.then(renderApp);
      }
      void loader.prefetchRecruiterLocale(cached);
      return appModule.then((module) =>
        renderWhenReady(module, loader.whenInitialRecruiterLanguageApplied())
      );
    })
    .catch(renderAppLoadFailure);
};

if (
  import.meta.env.REACT_APP_SALV_APP !== 'internal' &&
  import.meta.env.REACT_APP_SALV_APP !== 'jobboard' &&
  window.location.pathname === '/external-agent/chat'
) {
  configureAppTransport(externalAgentTransportPolicy);
  renderWhenLocaleReady(() =>
    Promise.all([import('./ExternalAgentApp'), import('./i18n')]).then(
      ([appModule, { ensureEnglishCommon }]) =>
        ensureEnglishCommon().then(() => appModule)
    )
  );
} else if (import.meta.env.REACT_APP_SALV_APP === 'interview') {
  configureAppTransport(interviewTransportPolicy);
  // Before the app chunk arrives, so the link's API requests overlap its
  // download; RouteWrapper and the page then join or take them. A dynamic
  // import so the other apps' entries do not reach its modules; the interview
  // build puts it in this entry's own chunk (vite.config.ts), so it resolves
  // without a request.
  import('./candidate-app/startInterviewBootRequests')
    .then(({ startInterviewBootRequests }) => startInterviewBootRequests())
    .catch((error: unknown) => {
      // swallow-ok: an optimization only; RouteWrapper and the page send each request themselves
      console.warn('Interview boot requests did not start', error);
    });
  renderWhenLocaleReady(() => import('./InterviewApp'));
} else if (import.meta.env.REACT_APP_SALV_APP === 'jobboard') {
  configureAppTransport(jobBoardTransportPolicy);
  renderWhenLocaleReady(() => import('./JobBoardApp'));
} else if (import.meta.env.REACT_APP_SALV_APP === 'internal') {
  configureAppTransport(internalTransportPolicy);
  import('./InternalApp').then(renderApp).catch(renderAppLoadFailure);
} else {
  configureAppTransport(recruiterTransportPolicy);
  // The warm-up is a dynamic import so the other apps' builds never reach the
  // route table and its page imports; the recruiter build puts it in this
  // entry's own chunk (vite.config.ts), so it resolves without a request.
  renderRecruiterApp(
    () => import('./App'),
    () => import('./app-shell/preloadRouteChunk')
  );
}
